import { Link } from 'react-router-dom';
import { AlertTriangle, TrendingDown } from 'lucide-react';
import { PageHeader, PageBody } from '@/components/layout/AppShell';
import { Card, CardHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { TierBadge } from '@/components/ui/TierBadge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { useCosts } from '@/api/costs';
import { TENANT_STATUS_LABEL, TENANT_STATUS_TONE } from '@/lib/tier';
import type { Tier, TenantStatus } from '@/data/types';
import { cn } from '@/lib/cn';

const usd = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export function CostsRoute() {
  const { data } = useCosts();
  const customers = data?.customers ?? [];
  const totals = data?.totals;

  return (
    <>
      <PageHeader
        eyebrow="Internal"
        title="Costs & revenue"
        description={
          <>
            What each customer costs to run (computed live from their actual
            server sizes at DigitalOcean list prices) versus what their plan
            charges. Negative margins are flagged — those customers cost more
            to host than they pay.
          </>
        }
      />
      <PageBody>
        {data?.pricing_is_placeholder && (
          <div className="mb-4 rounded-lg border border-[color:var(--warn)]/40 bg-[color:var(--warn-soft)] px-4 py-2.5 flex items-center gap-3 text-sm">
            <AlertTriangle size={16} className="text-[color:var(--warn)] shrink-0" />
            <div>
              <strong>Plan prices are placeholders.</strong> Infra costs are real;
              the revenue and margin columns use stand-in prices until real ones are
              set on the <a href="/plans" className="underline">Plans page</a>.
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 mb-4">
          <Stat label="Monthly infra cost" value={totals ? usd(totals.infra_monthly_usd) : '—'} hint="Real — from live server sizes" />
          <Stat label="Monthly plan revenue" value={totals ? usd(totals.revenue_monthly_usd) : '—'} hint={data?.pricing_is_placeholder ? 'Placeholder prices' : 'At current plan prices'} />
          <Stat
            label="Monthly margin"
            value={totals ? usd(totals.margin_monthly_usd) : '—'}
            hint="Revenue minus infra"
            tone={totals && totals.margin_monthly_usd < 0 ? 'bad' : 'good'}
          />
          <Stat
            label="Unpriced customers"
            value={totals ? String(totals.unpriced_customers) : '—'}
            hint="Enterprise / custom contracts"
          />
        </div>

        <Card>
          <CardHeader
            title="Per customer"
            description="Sorted by infra cost, highest first. Click a customer to manage their plan in Settings."
          />
          <Table>
            <THead>
              <tr>
                <TH>Customer</TH>
                <TH>Plan</TH>
                <TH className="text-right">Instances</TH>
                <TH className="text-right">Infra $/mo</TH>
                <TH className="text-right">Plan $/mo</TH>
                <TH className="text-right">Margin $/mo</TH>
              </tr>
            </THead>
            <TBody>
              {customers.map(c => {
                const negative = c.margin_monthly_usd !== null && c.margin_monthly_usd < 0;
                return (
                  <TR key={c.tenant_id}>
                    <TD>
                      <Link to={`/tenants/${c.slug}`} className="font-medium text-fg hover:text-accent">
                        {c.name}
                      </Link>
                      <Badge tone={TENANT_STATUS_TONE[c.status as TenantStatus] ?? 'neutral'} className="ml-2">
                        {TENANT_STATUS_LABEL[c.status as TenantStatus] ?? c.status}
                      </Badge>
                    </TD>
                    <TD><TierBadge tier={c.tier as Tier} /></TD>
                    <TD className="text-right tabnum">{c.instances}</TD>
                    <TD className="text-right tabnum">{usd(c.infra_monthly_usd)}</TD>
                    <TD className="text-right tabnum">
                      {c.plan_monthly_usd === null
                        ? <span className="text-fg-subtle" title="Enterprise / custom contract — no list price">custom</span>
                        : usd(c.plan_monthly_usd)}
                    </TD>
                    <TD className={cn('text-right tabnum font-medium', negative && 'text-[color:var(--danger)]')}>
                      {c.margin_monthly_usd === null ? '—' : (
                        <>
                          {negative && <TrendingDown size={12} className="inline mr-1 -mt-0.5" />}
                          {usd(c.margin_monthly_usd)}
                        </>
                      )}
                    </TD>
                  </TR>
                );
              })}
              {customers.length === 0 && (
                <TR><TD colSpan={6} className="text-center text-fg-muted py-6">No customers yet.</TD></TR>
              )}
            </TBody>
          </Table>
        </Card>
      </PageBody>
    </>
  );
}

function Stat({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: 'good' | 'bad' }) {
  return (
    <Card className="px-4 py-3.5">
      <div className="text-2xs uppercase tracking-wider text-fg-subtle font-semibold">{label}</div>
      <div className="text-2xl font-semibold tabnum tracking-tight mt-1">{value}</div>
      {hint && (
        <div className={
          tone === 'good' ? 'text-xs mt-0.5 text-[color:var(--success)]' :
          tone === 'bad' ? 'text-xs mt-0.5 text-[color:var(--danger)]' :
          'text-xs mt-0.5 text-fg-muted'
        }>
          {hint}
        </div>
      )}
    </Card>
  );
}
